import { useEffect, useMemo, useState } from 'react';
import { payouts, api } from '../lib/api';
import { toast } from 'sonner';
import {
  Wallet, Clock, CheckCircle2, Truck, Download, Calendar,
  Landmark, Smartphone, Info, Loader2, RefreshCw, TrendingUp, ShieldAlert, IndianRupee,
} from 'lucide-react';

const STATE_STYLES = {
  available:        { cls: 'bg-[#E8F5E9] text-[#1B5E20]',    label: 'Available' },
  clearing:         { cls: 'bg-[#FFF3E0] text-[#9A4500]',    label: 'Clearing (T+7)' },
  pending_delivery: { cls: 'bg-[#E3F2FD] text-[#0D47A1]',    label: 'Awaiting delivery' },
  paid_out:         { cls: 'bg-[#EDE7F6] text-[#4527A0]',    label: 'Paid out' },
  cancelled:        { cls: 'bg-muted text-ink/60',           label: 'Cancelled' },
  refunded:         { cls: 'bg-[#FBE9E7] text-[#BF360C]',    label: 'Refunded' },
};

const inr = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const target = new Date(dateStr + 'T00:00:00Z');
  const now = new Date();
  const diff = Math.ceil((target - now) / 86400000);
  return diff;
}

function fmtDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr.length === 10 ? dateStr + 'T00:00:00Z' : dateStr);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function PayoutDashboard() {
  const [summary, setSummary]   = useState(null);
  const [rows, setRows]         = useState(null);
  const [history, setHistory]   = useState(null);
  const [stateFilter, setStateFilter] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [csvBusy, setCsvBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, h] = await Promise.all([
        payouts.summary(90),
        payouts.history(20),
      ]);
      setSummary(s);
      setHistory(h);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load payouts');
    } finally {
      setLoading(false);
    }
  };

  const loadRows = async () => {
    try {
      const params = { page, page_size: 20 };
      if (stateFilter) params.state = stateFilter;
      setRows(await payouts.orders(params));
    } catch (e) { console.warn('[payouts] loadRows failed', e?.response?.status); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { loadRows(); /* eslint-disable-next-line */ }, [stateFilter, page]);

  const downloadStatement = async () => {
    setCsvBusy(true);
    try {
      const res = await api.get('/owner/payouts/statement.csv', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mandispot-payout-statement-${Date.now()}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Statement download failed');
    } finally { setCsvBusy(false); }
  };

  const maxDay = useMemo(() => {
    if (!summary?.by_day?.length) return 1;
    return Math.max(...summary.by_day.map(d => d.amount || 0)) || 1;
  }, [summary]);

  const daysToPayout = daysUntil(summary?.next_payout?.date);
  const kyc = summary?.bank?.kyc_status;
  const showBankWarning = kyc !== 'verified' || (!summary?.bank?.bank_last4 && !summary?.bank?.upi_id);

  if (loading && !summary) {
    return <div className="card-soft p-8 text-sm text-ink/60 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/>Crunching your earnings…</div>;
  }

  return (
    <section data-testid="payout-dashboard" className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-3 flex-wrap">
        <div className="w-12 h-12 rounded-xl bg-[#1B5E20] text-white grid place-items-center"><Wallet className="w-6 h-6"/></div>
        <div className="flex-1 min-w-[200px]">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#1B5E20]">Payouts</div>
          <h2 className="font-heading font-bold text-2xl">Your earnings &amp; bank settlements</h2>
          <p className="text-xs text-ink/60 mt-1">Money settles <b>T+{summary?.settlement?.hold_days ?? 7}</b> after delivery &amp; is paid every <b>Friday</b> to your registered UPI / bank account.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="payouts-refresh" onClick={load} className="btn-outline"><RefreshCw className="w-4 h-4"/>Refresh</button>
          <button data-testid="payouts-download-statement" onClick={downloadStatement} disabled={csvBusy} className="btn-primary disabled:opacity-60">
            {csvBusy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Download className="w-4 h-4"/>}
            Statement
          </button>
        </div>
      </div>

      {showBankWarning && (
        <div data-testid="payouts-kyc-warning" className="rounded-2xl border-2 border-[#FFCC80] bg-[#FFF3E0] p-4 flex items-start gap-3">
          <ShieldAlert className="w-5 h-5 text-[#9A4500] flex-shrink-0 mt-0.5"/>
          <div className="text-sm text-[#9A4500]">
            <div className="font-semibold">Complete KYC &amp; bank details to receive payouts</div>
            <div className="text-xs mt-1">BharatCart cannot release your earnings until we&apos;ve verified your bank/UPI. Head to the <b>KYC &amp; Bank</b> tab to finish setup — it takes 2 minutes.</div>
          </div>
        </div>
      )}

      {/* NEXT PAYOUT — the hero card */}
      <div data-testid="next-payout-card" className="rounded-3xl overflow-hidden border-2 border-[#1B5E20]/20 bg-gradient-to-br from-[#0D3B15] to-[#1B5E20] text-white p-6 md:p-8">
        <div className="flex items-start gap-6 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <div className="text-[10px] uppercase tracking-[0.24em] font-bold opacity-70">Next payout</div>
            <div className="mt-1 font-heading font-black text-4xl md:text-5xl tabular-nums" data-testid="next-payout-amount">
              {inr(summary?.next_payout?.amount)}
            </div>
            <div className="mt-2 text-sm opacity-80 flex items-center gap-2">
              <Calendar className="w-4 h-4"/>
              {summary?.next_payout?.date
                ? <>Scheduled <b>{fmtDate(summary.next_payout.date)}</b>{typeof daysToPayout === 'number' && daysToPayout >= 0 && <span className="opacity-60"> · in {daysToPayout} day{daysToPayout === 1 ? '' : 's'}</span>}</>
                : <>No payout scheduled yet — orders will start clearing after delivery.</>
              }
            </div>
          </div>
          <div className="flex flex-col gap-2 text-xs">
            <div className="rounded-lg bg-white/10 backdrop-blur px-3 py-2">
              <div className="opacity-70">To</div>
              <div className="font-semibold flex items-center gap-1.5 mt-0.5">
                {summary?.bank?.preferred_method === 'upi' ? <Smartphone className="w-3.5 h-3.5"/> : <Landmark className="w-3.5 h-3.5"/>}
                {summary?.bank?.preferred_method === 'upi'
                  ? (summary?.bank?.upi_id || '—')
                  : (summary?.bank?.bank_last4 ? `A/c •••• ${summary.bank.bank_last4}` : 'Bank not linked')
                }
              </div>
              {summary?.bank?.bank_ifsc && summary?.bank?.preferred_method !== 'upi' && (
                <div className="opacity-60 mt-0.5">{summary.bank.bank_ifsc}</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi testId="kpi-available"        Icon={CheckCircle2} tone="green"  label="Available now"   value={summary?.kpi?.available_balance} sub={`${summary?.orders_by_state?.available || 0} orders ready`}/>
        <Kpi testId="kpi-clearing"         Icon={Clock}        tone="orange" label="Clearing (T+7)"  value={summary?.kpi?.clearing} sub={`${summary?.orders_by_state?.clearing || 0} orders in hold`}/>
        <Kpi testId="kpi-pending-delivery" Icon={Truck}        tone="blue"   label="Awaiting delivery" value={summary?.kpi?.pending_delivery} sub={`${summary?.orders_by_state?.pending_delivery || 0} in transit`}/>
        <Kpi testId="kpi-lifetime"         Icon={TrendingUp}   tone="violet" label="Lifetime paid out" value={summary?.kpi?.lifetime_paid_out} sub={`${summary?.orders_by_state?.paid_out || 0} orders settled`}/>
      </div>

      {/* Weekly trend */}
      <div className="card-soft p-5">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="font-heading font-bold text-lg inline-flex items-center gap-2"><TrendingUp className="w-5 h-5"/>Delivered earnings (last 90 days)</div>
          <div className="text-xs text-ink/60">Total <b>{inr(summary?.kpi?.lifetime_earned)}</b> earned since day one</div>
        </div>
        {!summary?.by_day?.length ? (
          <div className="text-ink/50 text-sm">No deliveries yet in this window. Start selling to see your trend line grow!</div>
        ) : (
          <div className="flex items-end gap-1 h-32 overflow-x-auto pb-2">
            {summary.by_day.map(d => {
              const h = Math.max(4, Math.round((d.amount / maxDay) * 108));
              return (
                <div key={d.date} data-testid={`payout-bar-${d.date}`} className="group flex flex-col items-center min-w-[24px] cursor-pointer" title={`${fmtDate(d.date)}\n${inr(d.amount)} · ${d.orders} orders`}>
                  <div className="w-4 bg-gradient-to-t from-[#1B5E20] to-[#66BB6A] rounded-t group-hover:from-[#D35400] group-hover:to-[#FB8C00] transition-colors" style={{ height: `${h}px` }}/>
                  <div className="mt-1 text-[9px] text-ink/40 rotate-45 origin-left whitespace-nowrap">{d.date.slice(5)}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* How settlement works */}
      <div className="card-soft p-5 bg-[#FFF8EE] border border-[#F2E9D5]">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-[#9A4500] flex-shrink-0 mt-0.5"/>
          <div>
            <div className="font-heading font-bold text-base text-[#9A4500]">How your money moves</div>
            <ol className="mt-2 text-sm text-ink/70 space-y-1 list-decimal ml-4">
              <li><b>Order placed</b> → your product is packed &amp; delivered. Nothing settles yet.</li>
              <li><b>Delivered</b> → payout amount enters <span className="text-[#9A4500] font-semibold">Clearing</span> for T+7 days (buyer&apos;s return window closes).</li>
              <li><b>T+7 completes</b> → the amount moves to <span className="text-[#1B5E20] font-semibold">Available</span>.</li>
              <li><b>Every Friday</b> → BharatCart batches all Available money and transfers it to your UPI/bank. You&apos;ll see it in <span className="text-[#4527A0] font-semibold">Paid out</span>.</li>
            </ol>
            <div className="mt-3 pt-3 border-t border-[#F2E9D5] text-xs text-ink/60">
              <b className="text-[#1B5E20]">TCS ({summary?.settlement?.tcs_rate_pct ?? 1}%)</b> is deducted from your payout on every delivered order (Section 52, CGST Act 2017) &amp; remitted by BharatCart to Govt via GSTR-8 by the 10th of the next month. You can claim it back as input tax credit in your monthly GSTR-3B.
            </div>
          </div>
        </div>
      </div>

      {/* Orders table with state filter */}
      <div className="card-soft overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between flex-wrap gap-3">
          <div className="font-heading font-bold text-lg">Order-by-order breakdown</div>
          <div className="flex gap-1 flex-wrap">
            {[
              { k: '',                  label: 'All' },
              { k: 'available',         label: 'Available' },
              { k: 'clearing',          label: 'Clearing' },
              { k: 'pending_delivery',  label: 'Awaiting delivery' },
              { k: 'paid_out',          label: 'Paid out' },
              { k: 'refunded',          label: 'Refunded' },
            ].map(({ k, label }) => (
              <button
                key={k || 'all'}
                data-testid={`payouts-filter-${k || 'all'}`}
                onClick={() => { setStateFilter(k); setPage(1); }}
                className={`px-3 py-1 text-xs rounded-full border transition ${stateFilter === k ? 'bg-[#1B5E20] text-white border-[#1B5E20]' : 'bg-white text-ink/70 border-border hover:border-[#1B5E20]/40'}`}
              >{label}</button>
            ))}
          </div>
        </div>

        {!rows?.rows?.length ? (
          <div className="p-10 text-center text-ink/60">
            <Wallet className="w-10 h-10 mx-auto text-ink/30"/>
            <div className="font-heading font-bold text-base mt-3">
              {stateFilter ? `No orders in "${STATE_STYLES[stateFilter]?.label || stateFilter}"` : 'No orders yet'}
            </div>
            <div className="text-xs text-ink/50 mt-1">As buyers place &amp; receive orders, your earnings will show up here.</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-ink/50 border-b border-border">
                  <th className="py-2.5 px-4">Order</th>
                  <th className="py-2.5 px-4">Delivered on</th>
                  <th className="py-2.5 px-4 text-right">Order value</th>
                  <th className="py-2.5 px-4 text-right">Commission</th>
                  <th className="py-2.5 px-4 text-right">TCS (1%)</th>
                  <th className="py-2.5 px-4 text-right">You earn</th>
                  <th className="py-2.5 px-4">Status</th>
                  <th className="py-2.5 px-4">Payout date</th>
                </tr>
              </thead>
              <tbody>
                {rows.rows.map(r => {
                  const st = STATE_STYLES[r.settlement_state] || { cls: 'bg-muted text-ink/60', label: r.settlement_state || '—' };
                  const payoutDate = r.payout_paid_at ? (r.payout_paid_at.slice(0, 10)) : (r.expected_payout_date || null);
                  return (
                    <tr key={r.order_id} data-testid={`payout-row-${r.order_id}`} className="border-b border-border/40 hover:bg-[#FFF8E1]/40">
                      <td className="py-2.5 px-4">
                        <div className="font-mono text-[11px]">#{r.order_id.slice(0, 8)}</div>
                        <div className="text-[10px] text-ink/40">{r.customer_name || 'Customer'}</div>
                      </td>
                      <td className="py-2.5 px-4 text-[11px] text-ink/60">{r.delivered_at ? fmtDate(r.delivered_at) : (r.created_at ? <span className="italic opacity-70">— placed {fmtDate(r.created_at)}</span> : '—')}</td>
                      <td className="py-2.5 px-4 text-right tabular-nums">{inr(r.total_order_value)}</td>
                      <td className="py-2.5 px-4 text-right tabular-nums text-ink/60">−{inr((r.commission_earned || 0) + (r.commission_gst || 0))}</td>
                      <td className="py-2.5 px-4 text-right tabular-nums text-[#9A4500]" title="Tax Collected at Source u/s 52 CGST — remitted via GSTR-8">−{inr(r.tcs_amount)}</td>
                      <td className="py-2.5 px-4 text-right tabular-nums font-semibold text-[#1B5E20]">{inr(r.merchant_payout)}</td>
                      <td className="py-2.5 px-4"><span className={`pill !text-[9px] ${st.cls}`}>{st.label}</span></td>
                      <td className="py-2.5 px-4 text-[11px] text-ink/60">
                        {r.payout_paid_at
                          ? <span className="text-[#4527A0]">Paid {fmtDate(r.payout_paid_at)}</span>
                          : payoutDate ? fmtDate(payoutDate) : '—'
                        }
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {rows.total > rows.page_size && (
              <div className="px-5 py-3 flex items-center justify-between text-xs border-t border-border/40">
                <span className="text-ink/60">Page {rows.page} of {Math.ceil(rows.total / rows.page_size)}</span>
                <div className="flex gap-2">
                  <button data-testid="payouts-prev-page" disabled={rows.page <= 1} onClick={() => setPage(p => p - 1)} className="btn-outline !py-1.5 disabled:opacity-40">Prev</button>
                  <button data-testid="payouts-next-page" disabled={rows.page >= Math.ceil(rows.total / rows.page_size)} onClick={() => setPage(p => p + 1)} className="btn-outline !py-1.5 disabled:opacity-40">Next</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Payout history */}
      <div className="card-soft p-5">
        <div className="font-heading font-bold text-lg inline-flex items-center gap-2"><IndianRupee className="w-5 h-5"/>Past payout batches</div>
        <div className="mt-3">
          {!history?.batches?.length ? (
            <div className="text-sm text-ink/60">No payouts have been transferred yet — your first Friday transfer is on the way!</div>
          ) : (
            <div className="space-y-2">
              {history.batches.map(b => (
                <div key={b.batch_id} data-testid={`payout-batch-${b.batch_id}`} className="flex items-center gap-3 rounded-lg border border-border bg-white p-3">
                  <div className="w-9 h-9 rounded-full bg-[#EDE7F6] text-[#4527A0] grid place-items-center">
                    {b.method === 'upi' ? <Smartphone className="w-4 h-4"/> : <Landmark className="w-4 h-4"/>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm">{inr(b.amount)} <span className="text-xs text-ink/60 font-normal">· {b.orders} order{b.orders === 1 ? '' : 's'}</span></div>
                    <div className="text-[11px] text-ink/50">
                      {fmtDate(b.paid_at)} · {(b.method || 'bank').toUpperCase()}
                      {b.reference && <> · Ref <span className="font-mono">{b.reference}</span></>}
                    </div>
                  </div>
                  <span className="pill !text-[9px] bg-[#E8F5E9] text-[#1B5E20]">Settled</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function Kpi({ testId, Icon, label, value, sub, tone }) {
  const styles = {
    blue:   'bg-[#E3F2FD] border-[#90CAF9] text-[#0D47A1]',
    orange: 'bg-[#FFF3E0] border-[#FFCC80] text-[#9A4500]',
    green:  'bg-[#E8F5E9] border-[#A5D6A7] text-[#1B5E20]',
    violet: 'bg-[#EDE7F6] border-[#B39DDB] text-[#4527A0]',
  }[tone] || 'bg-white border-border text-ink';
  return (
    <div data-testid={testId} className={`rounded-2xl border-2 p-4 ${styles}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-80">{label}</div>
        <Icon className="w-4 h-4 opacity-70"/>
      </div>
      <div className="font-heading font-black text-2xl mt-2 tabular-nums">{inr(value)}</div>
      <div className="text-[11px] opacity-70 mt-1 leading-snug">{sub}</div>
    </div>
  );
}
